//* Libraries imports
import { CreditCard, QrCode, Barcode, Landmark } from "lucide-react";

//* Schemas imports
import { PaymentStatus } from "@/schemas/payment";
import { MethodPaymentEnum } from "@/schemas/methodpayment";

export const PAYMENT_METHOD_ICONS = {
  [MethodPaymentEnum.CREDIT_CARD]: CreditCard,
  [MethodPaymentEnum.PIX]: QrCode,
  [MethodPaymentEnum.BOLETO]: Barcode,
  [MethodPaymentEnum.TED]: Landmark,
};

export const PAYMENT_METHOD_COLORS = {
  [MethodPaymentEnum.CREDIT_CARD]: "text-blue-600 bg-blue-50 border-blue-100",
  [MethodPaymentEnum.PIX]: "text-teal-600 bg-teal-50 border-teal-100",
  [MethodPaymentEnum.BOLETO]: "text-orange-600 bg-orange-50 border-orange-100",
  [MethodPaymentEnum.TED]: "text-purple-600 bg-purple-50 border-purple-100",
};

export const getStatusLabel = (status: PaymentStatus) => {
  switch (status) {
    case "CREATED":
      return "COBRANÇA GERADA";
    case "PENDING":
      return "AGUARDANDO CONFIRMAÇÃO";
    case "RECEIVED":
      return "COBRANÇA RECEBIDA";
    case "CONFIRMED":
      return "COBRANÇA CONFIRMADA";
    case "TED":
      return "AGUARDANDO CONFIRMAÇÃO DE TRANSFERÊNCIA (TED)";
    case "CHECKOUT_VIEWED":
      return "COBRANÇA VIZUALIZADA";
    case "DELETED":
      return "COBRANÇA DELETADA";
    case "OVERDUE":
      return "COBRANÇA VENCIDO";
    case "REFUNDED":
      return "COBRANÇA REEMBOLSADA";
    case "RECEIVED_IN_CASH":
      return "COBRANÇA RECEBIDA EM DINHEIRO";
    case "REFUND_REQUESTED":
      return "COBRANÇA SOLICITADA";
    case "REFUND_IN_PROGRESS":
      return "COBRANÇA REEMBOLSANDO";
    case "CHARGEBACK_REQUESTED":
      return "COBRANÇA SOLICITADA";
    case "CHARGEBACK_DISPUTE":
      return "COBRANÇA DISPUTA DE CHARGEBACK";
    case "AWAITING_CHARGEBACK_REVERSAL":
      return "COBRANÇA AGUARDANDO REVERSÃO DE CHARGEBACK";
    case "DUNNING_REQUESTED":
      return "COBRANÇA SOLICITADA";
    case "DUNNING_RECEIVED":
      return "COBRANÇA RECEBIDA";
    case "AWAITING_RISK_ANALYSIS":
      return "COBRANÇA AGUARDANDO ANÁLISE DE RISCO";
    default:
      return status;
  }
};

export const getStatusColor = (status: PaymentStatus) => {
  switch (status) {
    case "CREATED":
      return "#3b82f6"; // blue-500
    case "PENDING":
      return "#f59e0b"; // amber-500
    case "RECEIVED":
      return "#22c55e"; // green-500
    case "CONFIRMED":
      return "#059669"; // emerald-600
    case "OVERDUE":
      return "#ef4444"; // red-500
    case "REFUNDED":
      return "#64748b"; // slate-500
    case "RECEIVED_IN_CASH":
      return "#65a30d"; // lime-600
    case "REFUND_REQUESTED":
      return "#fb923c"; // orange-400
    case "REFUND_IN_PROGRESS":
      return "#f97316"; // orange-500
    case "CHARGEBACK_REQUESTED":
      return "#f43f5e"; // rose-500
    case "CHARGEBACK_DISPUTE":
      return "#e11d48"; // rose-600
    case "AWAITING_CHARGEBACK_REVERSAL":
      return "#be123c"; // rose-700
    case "DUNNING_REQUESTED":
      return "#a855f7"; // purple-500
    case "DUNNING_RECEIVED":
      return "#7c3aed"; // violet-600
    case "AWAITING_RISK_ANALYSIS":
      return "#6366f1"; // indigo-500
    case "TED":
      return "#eab308"; // yellow-500
    case "CHECKOUT_VIEWED":
      return "#ca8a04"; // yellow-600
    case "DELETED":
      return "#94a3b8"; // slate-400
    default:
      return "#64748b"; // slate-500
  }
};

export const getStatusBgColor = (status: PaymentStatus) => {
  switch (status) {
    case "CREATED":
      return "bg-blue-50 text-blue-700 border-blue-100";
    case "PENDING":
      return "bg-amber-50 text-amber-700 border-amber-100";
    case "RECEIVED":
      return "bg-green-50 text-green-700 border-green-100";
    case "CONFIRMED":
      return "bg-emerald-50 text-emerald-700 border-emerald-100";
    case "OVERDUE":
      return "bg-red-50 text-red-700 border-red-100";
    case "REFUNDED":
      return "bg-slate-50 text-slate-700 border-slate-100";
    case "RECEIVED_IN_CASH":
      return "bg-lime-50 text-lime-700 border-lime-100";
    case "REFUND_REQUESTED":
      return "bg-orange-50 text-orange-700 border-orange-100";
    case "REFUND_IN_PROGRESS":
      return "bg-orange-50 text-orange-700 border-orange-100";
    case "CHARGEBACK_REQUESTED":
      return "bg-rose-50 text-rose-700 border-rose-100";
    case "CHARGEBACK_DISPUTE":
      return "bg-rose-50 text-rose-700 border-rose-100";
    case "AWAITING_CHARGEBACK_REVERSAL":
      return "bg-rose-100 text-rose-800 border-rose-200";
    case "DUNNING_REQUESTED":
      return "bg-purple-50 text-purple-700 border-purple-100";
    case "DUNNING_RECEIVED":
      return "bg-violet-50 text-violet-700 border-violet-100";
    case "AWAITING_RISK_ANALYSIS":
      return "bg-indigo-50 text-indigo-700 border-indigo-100";
    case "TED":
      return "bg-yellow-50 text-yellow-700 border-yellow-100";
    case "CHECKOUT_VIEWED":
      return "bg-yellow-600 text-yellow-100 border-yellow-700";
    case "DELETED":
      return "bg-gray-50 text-gray-700 border-gray-100";
    default:
      return "bg-gray-50 text-gray-700 border-gray-100";
  }
};

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};
